import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronRight, CheckCircle2, Globe, Smartphone, Shield, Zap, Search, MessageCircle, BarChart3, Clock, Settings, FileText, Headphones, Award } from 'lucide-react';

const WordPressServices = () => {
  const deliverables = [
    { item: 'Custom WordPress design', details: 'Modern, mobile-friendly, fully responsive' },
    { item: 'Page setup', details: 'Home, About Us, Services/Products, Blog, Contact' },
    { item: 'Elementor Pro integration', details: 'Drag-and-drop editing for easy future updates' },
    { item: 'SSL & security', details: 'HTTPS setup, firewall, brute-force protection' },
    { item: 'Contact forms', details: 'Spam-protected lead capture' },
    { item: 'WhatsApp/chat integration', details: 'Optional, for instant customer connect' },
    { item: 'On-page SEO', details: 'Titles, meta descriptions, alt tags, internal linking' },
    { item: 'Google setup', details: 'Search Console verification + Analytics tracking' },
    { item: 'Speed optimization', details: 'Caching, image compression, CDN (if required)' },
    { item: 'Sitemap & indexing', details: 'XML sitemap submission to Google' },
  ];

  const timeline = [
    { phase: 'Design & Development', duration: '7 days' },
    { phase: 'SEO & Google Integrations', duration: '3 days' },
    { phase: 'Total', duration: '7–10 days' },
  ];

  const postLaunchSupport = [
    'Bug and error fixes',
    'WordPress, theme & plugin updates',
    'Monthly speed and performance checks',
    'Search Console & Analytics monitoring',
    'Minor content updates (text/images)',
  ];

  const whyChooseUs = [
    { icon: Award, title: '7+ Years E-commerce Experience', description: 'We know what converts — same team managing your marketplace accounts now builds your website.' },
    { icon: Search, title: 'SEO From Day One', description: 'Same team handling your Amazon/Flipkart SEO now builds your website SEO from the start.' },
    { icon: Globe, title: 'One Partner, Full Coverage', description: 'One partner for your marketplace listings and your brand website — seamless coordination.' },
    { icon: Shield, title: 'Transparent Pricing', description: 'One-time investment with no recurring surprises. Know exactly what you\'re paying for.' },
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>WordPress Website Development Services | Obiquiste Solutions</title>
        <meta name="description" content="Professional WordPress website development for e-commerce brands. Fast, SEO-ready websites live in 7-10 days. Custom design, Elementor Pro, security & speed optimization included." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/wordpress-website-development" />
      </Helmet>
      
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center text-sm text-slate-600">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/services" className="hover:text-primary">Services</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-blue-600 font-medium">WordPress Development</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Globe className="w-4 h-4 mr-2" />
                Website Development
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                WordPress Website <span className="text-blue-600">Development</span>
              </h1>
              <p className="text-xl md:text-2xl text-slate-700 mb-4 font-medium">
                Turn your brand into a website that sells — live in 7–10 days.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                You've built your presence on Amazon, Flipkart, and Myntra. Now give your brand a home of its own — a fast, SEO-ready WordPress website that converts visitors into customers and gives you a channel no marketplace controls.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?service=wordpress">
                  <Button className="bg-blue-600 text-white hover:bg-blue-700 px-8 py-6 rounded-full text-base font-semibold">
                    Get Free Consultation
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              What You Get
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              A complete WordPress website package with everything your brand needs to succeed online.
            </p>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] border-collapse bg-white rounded-xl shadow-sm">
              <thead>
                <tr className="bg-blue-50">
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-blue-100">Deliverable</th>
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-blue-100">Details</th>
                </tr>
              </thead>
              <tbody>
                {deliverables.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-blue-50/50 transition-colors">
                    <td className="py-4 px-6">
                      <span className="font-semibold text-slate-800">{item.item}</span>
                    </td>
                    <td className="py-4 px-6 text-slate-600">{item.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Clock className="w-4 h-4 mr-2" />
                Project Timeline
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Live in Just 7–10 Days
              </h2>
              <p className="text-slate-600 mb-8">
                We follow a streamlined process to get your website up and running quickly without compromising on quality.
              </p>
              <div className="space-y-4">
                {timeline.map((item, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-4 rounded-xl ${idx === timeline.length - 1 ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200'}`}>
                    <span className={`font-semibold ${idx === timeline.length - 1 ? 'text-white' : 'text-slate-800'}`}>{item.phase}</span>
                    <span className={`font-bold ${idx === timeline.length - 1 ? 'text-blue-100' : 'text-blue-600'}`}>{item.duration}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl p-8 text-white"
            >
              <div className="flex items-center gap-3 mb-6">
                <Headphones className="w-8 h-8 text-blue-200" />
                <h3 className="font-heading text-2xl font-bold">Post-Launch Support</h3>
              </div>
              <p className="text-blue-100 mb-6 text-lg">1 Month Free Support Included</p>
              <ul className="space-y-4">
                {postLaunchSupport.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-200 mt-0.5 flex-shrink-0" />
                    <span className="text-blue-50">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Why Obiquiste for Your Website
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-lg transition-shadow text-center"
              >
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Build Your Brand's Website?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Get a fast, SEO-ready WordPress website that converts visitors into customers. Live in just 7-10 days.
          </p>
          <Link to="/contact?service=wordpress">
            <Button className="bg-white text-blue-600 hover:bg-slate-100 px-8 py-6 rounded-full text-base font-semibold">
              Request Free Consultation
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WordPressServices;
